// ---------------------------------------------------------------------------
// CM-T.1: Space Detail — feed, posts, reactions, comments, membership
// ---------------------------------------------------------------------------
import { useEffect, useId, useState } from "react";
import { useParams, Link } from "react-router";
import { formatDate, formatNumber } from "@/lib/i18n";
import {
  useSpace,
  useCommunityFeed,
  usePostComments,
  createPost,
  createComment,
  toggleReaction,
  joinSpace,
  leaveSpace,
  updateSpaceSettings,
  resetSpaceFromTemplate,
  type CommunityPost,
  type CommunityComment,
  type CommunitySpace,
} from "@/hooks/use-community";
import {
  ArrowLeft,
  Bot,
  Users,
  MessageSquare,
  ThumbsUp,
  Heart,
  Flame,
  Award,
  Send,
  Loader2,
  Pin,
  Lock,
  ChevronDown,
  ChevronUp,
  Plus,
  Sparkles,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const REACTION_CONFIG = [
  { type: "like", Icon: ThumbsUp, label: "Like" },
  { type: "love", Icon: Heart, label: "Love" },
  { type: "fire", Icon: Flame, label: "Fire" },
  { type: "celebrate", Icon: Award, label: "Celebrate" },
] as const;

const POST_TYPE_OPTIONS = [
  { value: "post", label: "Post" },
  { value: "poll", label: "Poll" },
  { value: "question", label: "Question" },
] as const;

const POST_TYPE_BADGE: Record<string, { label: string; className: string }> = {
  post: { label: "", className: "" },
  poll: { label: "Poll", className: "bg-[var(--color-ai)]/10 text-[var(--color-ai)]" },
  question: { label: "Question", className: "bg-[var(--color-warning)]/10 text-[var(--color-warning)]" },
  milestone: { label: "Milestone", className: "bg-[var(--color-success)]/10 text-[var(--color-success)]" },
  announcement: { label: "Announcement", className: "bg-[var(--color-primary)]/10 text-[var(--color-primary)]" },
};

function isAiAuthor(authorId: string | null | undefined): boolean {
  return typeof authorId === "string" && authorId.startsWith("ai:");
}

function canManageAiManager(space: CommunitySpace): boolean {
  return space.membershipRole === "admin" || space.membershipRole === "moderator";
}

// ---------------------------------------------------------------------------
// Comment Thread
// ---------------------------------------------------------------------------

function CommentThread({
  comment,
  postId,
  onCommentAdded,
}: {
  comment: CommunityComment;
  postId: string;
  onCommentAdded: () => void;
}) {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyContent, setReplyContent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const aiComment = isAiAuthor(comment.authorId);

  const handleReply = async () => {
    if (!replyContent.trim()) return;
    setIsSubmitting(true);
    try {
      await createComment(postId, { content: replyContent, parentId: comment.id });
      setReplyContent("");
      setShowReplyForm(false);
      onCommentAdded();
    } catch {
      // API error handled silently
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="border-l-2 border-[var(--color-border)] pl-4">
      <div className="flex items-start gap-2">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--color-muted)] text-xs font-medium text-[var(--color-foreground)]">
          {comment.authorName.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <span className="text-sm font-medium text-[var(--color-foreground)]">{comment.authorName}</span>
            {aiComment && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[var(--color-ai)]/10 px-2 py-0.5 text-[10px] font-medium text-[var(--color-ai)]">
                <Bot className="h-3 w-3" />
                Bot
              </span>
            )}
            <span className="text-[10px] text-[var(--color-muted-foreground)]">
              {formatDate(comment.createdAt)}
            </span>
          </div>
          {aiComment && (
            <p className="mt-0.5 text-[10px] text-[var(--color-muted-foreground)]">
              AI reply generated from your tenant knowledge base.
            </p>
          )}
          <p className="text-sm text-[var(--color-foreground)] mt-0.5">{comment.content}</p>
          <button
            type="button"
            onClick={() => setShowReplyForm(!showReplyForm)}
            className="mt-1 text-[10px] font-medium text-[var(--color-primary)] hover:underline"
          >
            Reply
          </button>

          {showReplyForm && (
            <div className="mt-2 flex gap-2">
              <input
                type="text"
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                className="flex-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-1.5 text-sm text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                placeholder="Write a reply..."
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleReply();
                  }
                }}
              />
              <button
                type="button"
                onClick={handleReply}
                disabled={isSubmitting || !replyContent.trim()}
                className="rounded-lg bg-[var(--color-primary)] px-3 py-1.5 text-sm text-white hover:bg-[var(--color-primary)]/90 disabled:opacity-50 transition-colors"
              >
                {isSubmitting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
              </button>
            </div>
          )}

          {/* Nested replies */}
          {comment.replies?.length > 0 && (
            <div className="mt-3 space-y-3">
              {comment.replies.map((reply) => (
                <CommentThread key={reply.id} comment={reply} postId={postId} onCommentAdded={onCommentAdded} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Comments Section
// ---------------------------------------------------------------------------

function CommentsSection({ postId }: { postId: string }) {
  const { comments, isLoading, refetch } = usePostComments(postId);
  const [newComment, setNewComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!newComment.trim()) return;
    setIsSubmitting(true);
    try {
      await createComment(postId, { content: newComment });
      setNewComment("");
      refetch();
    } catch {
      // API error
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mt-3 space-y-3 border-t border-[var(--color-border)] pt-3">
      {isLoading && (
        <div className="flex justify-center py-2">
          <Loader2 className="h-4 w-4 animate-spin text-[var(--color-primary)]" />
        </div>
      )}

      {comments.map((comment) => (
        <CommentThread key={comment.id} comment={comment} postId={postId} onCommentAdded={refetch} />
      ))}

      {/* New comment input */}
      <div className="flex gap-2">
        <input
          type="text"
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          className="flex-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
          placeholder="Write a comment..."
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSubmit();
            }
          }}
        />
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting || !newComment.trim()}
          className="rounded-lg bg-[var(--color-primary)] px-3 py-2 text-sm text-white hover:bg-[var(--color-primary)]/90 disabled:opacity-50 transition-colors"
        >
          {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Post Card
// ---------------------------------------------------------------------------

function PostCard({ post, onRefresh }: { post: CommunityPost; onRefresh: () => void }) {
  const [showComments, setShowComments] = useState(false);
  const [isReacting, setIsReacting] = useState(false);

  const badge = POST_TYPE_BADGE[post.postType];
  const aiPost = isAiAuthor(post.authorId);

  const handleReaction = async (reactionType: string) => {
    if (isReacting) return;
    setIsReacting(true);
    try {
      await toggleReaction(post.id, reactionType);
      onRefresh();
    } catch {
      // Silently handle
    } finally {
      setIsReacting(false);
    }
  };

  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
      {/* Author row */}
      <div className="flex items-start gap-3 mb-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--color-muted)] text-sm font-medium text-[var(--color-foreground)]">
          {post.authorName.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-[var(--color-foreground)]">{post.authorName}</span>
            {aiPost && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[var(--color-ai)]/10 px-2 py-0.5 text-[10px] font-medium text-[var(--color-ai)]">
                <Bot className="h-3 w-3" />
                Bot
              </span>
            )}
            {badge?.label && (
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${badge.className}`}>
                {badge.label}
              </span>
            )}
            {post.isPinned && <Pin className="h-3 w-3 text-[var(--color-warning)]" />}
            {post.isLocked && <Lock className="h-3 w-3 text-[var(--color-muted-foreground)]" />}
          </div>
          <span className="text-[10px] text-[var(--color-muted-foreground)]">
            {formatDate(post.createdAt)}
          </span>
        </div>
      </div>

      {/* Title + content */}
      {post.title && (
        <h3 className="text-base font-semibold text-[var(--color-foreground)] mb-1">{post.title}</h3>
      )}
      <p className="text-sm text-[var(--color-foreground)] whitespace-pre-wrap">{post.content}</p>

      {/* Reactions bar */}
      <div className="mt-4 flex items-center gap-1">
        {REACTION_CONFIG.map(({ type, Icon, label }) => (
          <button
            key={type}
            type="button"
            onClick={() => handleReaction(type)}
            disabled={isReacting}
            className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs transition-colors ${
              post.myReaction === type
                ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                : "text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]/30"
            }`}
            title={label}
          >
            <Icon className="h-3.5 w-3.5" />
          </button>
        ))}
        {post.reactionCount > 0 && (
          <span className="ml-1 text-xs text-[var(--color-muted-foreground)] tabular-nums">
            {post.reactionCount}
          </span>
        )}

        <div className="flex-1" />

        {/* Comments toggle */}
        <button
          type="button"
          onClick={() => setShowComments(!showComments)}
          className="flex items-center gap-1 text-xs text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-colors"
        >
          <MessageSquare className="h-3.5 w-3.5" />
          {post.commentCount > 0 && <span className="tabular-nums">{post.commentCount}</span>}
          {showComments ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>
      </div>

      {/* Comments section */}
      {showComments && <CommentsSection postId={post.id} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// New Post Form
// ---------------------------------------------------------------------------

function NewPostForm({ spaceId, onCreated }: { spaceId: string; onCreated: () => void }) {
  const [isOpen, setIsOpen] = useState(false);
  const [postType, setPostType] = useState<string>("post");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const typeId = useId();
  const titleId = useId();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await createPost(spaceId, {
        postType,
        title: title.trim() || undefined,
        content: content.trim(),
      });
      setTitle("");
      setContent("");
      setPostType("post");
      setIsOpen(false);
      onCreated();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Failed to publish post.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="w-full rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-background)] px-5 py-4 text-sm text-[var(--color-muted-foreground)] hover:border-[var(--color-primary)]/30 hover:text-[var(--color-foreground)] transition-colors text-left flex items-center gap-2"
      >
        <Plus className="h-4 w-4" />
        Share something with the community...
      </button>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-5 space-y-3"
    >
      <div className="flex items-center gap-3">
        <div>
          <label htmlFor={typeId} className="sr-only">Post type</label>
          <select
            id={typeId}
            value={postType}
            onChange={(e) => setPostType(e.target.value)}
            className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-1.5 text-sm text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
          >
            {POST_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className="flex-1">
          <label htmlFor={titleId} className="sr-only">Title</label>
          <input
            id={titleId}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-1.5 text-sm text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
            placeholder="Title (optional)"
          />
        </div>
      </div>

      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] resize-none"
        placeholder="Write your post..."
        rows={4}
        required
      />

      {submitError && (
        <p className="rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-3 py-2 text-sm text-[var(--color-destructive)]">
          {submitError}
        </p>
      )}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={() => {
            setIsOpen(false);
            setTitle("");
            setContent("");
          }}
          className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-muted)]/30 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting || !content.trim()}
          className="rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-primary)]/90 disabled:opacity-50 transition-colors"
        >
          {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Post"}
        </button>
      </div>
    </form>
  );
}

function AiManagerCard({
  space,
  onUpdated,
}: {
  space: CommunitySpace;
  onUpdated: () => void;
}) {
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const aiSettings = space.settings?.aiManager;

  if (!canManageAiManager(space) || !aiSettings) {
    return null;
  }

  const handleToggle = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      await updateSpaceSettings(space.id, {
        aiManager: {
          enabled: !aiSettings.enabled,
        },
      });
      onUpdated();
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "Failed to update AI manager settings.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[var(--color-ai)]" />
            <h2 className="text-sm font-semibold text-[var(--color-foreground)]">AI Manager</h2>
          </div>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            AI moderation screens new posts, and tenant knowledge base answers appear with a labeled Bot badge.
          </p>
          <p className="text-[11px] text-[var(--color-muted-foreground)]">
            EU AI Act disclosure: members can see when a reply was generated by AI.
          </p>
        </div>

        <button
          type="button"
          onClick={handleToggle}
          disabled={isSaving}
          className={`inline-flex min-w-28 items-center justify-center rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
            aiSettings.enabled
              ? "bg-[var(--color-ai)] text-white hover:bg-[var(--color-ai)]/90"
              : "border border-[var(--color-border)] text-[var(--color-foreground)] hover:bg-[var(--color-muted)]/30"
          }`}
        >
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : aiSettings.enabled ? "Enabled" : "Disabled"}
        </button>
      </div>

      <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-[var(--color-muted-foreground)]">
        <span className="rounded-full bg-[var(--color-muted)]/40 px-2.5 py-1">
          Auto-moderate: {aiSettings.autoModerate ? "On" : "Off"}
        </span>
        <span className="rounded-full bg-[var(--color-muted)]/40 px-2.5 py-1">
          Question replies: {aiSettings.autoReplyToQuestions ? "On" : "Off"}
        </span>
      </div>

      {saveError && (
        <p className="mt-3 rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-3 py-2 text-sm text-[var(--color-destructive)]">
          {saveError}
        </p>
      )}
    </div>
  );
}

function CommunityTemplateCard({
  space,
  onUpdated,
}: {
  space: CommunitySpace;
  onUpdated: () => void;
}) {
  const [isResetting, setIsResetting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  if (!canManageAiManager(space)) {
    return null;
  }

  const handleReset = async () => {
    setIsResetting(true);
    setResetError(null);
    try {
      await resetSpaceFromTemplate(space.id);
      onUpdated();
    } catch (error) {
      setResetError(error instanceof Error ? error.message : "Failed to reset the community template.");
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h2 className="text-sm font-semibold text-[var(--color-foreground)]">Community Template</h2>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Re-apply the default vertical template for this space if naming or description drifted from the onboarding baseline.
          </p>
        </div>

        <button
          type="button"
          onClick={handleReset}
          disabled={isResetting}
          className="inline-flex min-w-36 items-center justify-center rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-muted)]/30 disabled:opacity-50 transition-colors"
        >
          {isResetting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset from template"}
        </button>
      </div>

      {resetError && (
        <p className="mt-3 rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-3 py-2 text-sm text-[var(--color-destructive)]">
          {resetError}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Space Detail Page
// ---------------------------------------------------------------------------

export default function SpaceDetailPage() {
  const { spaceId } = useParams();
  const { space, isLoading: isLoadingSpace, refetch: refetchSpace } = useSpace(spaceId);
  const [page, setPage] = useState(1);
  const { posts, hasMore, isLoading: isLoadingPosts, refetch: refetchPosts } = useCommunityFeed(spaceId, page);
  const [isTogglingMembership, setIsTogglingMembership] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [spaceId]);

  const handleToggleMembership = async () => {
    if (!spaceId || !space) return;
    setIsTogglingMembership(true);
    try {
      if (space.isMember) {
        await leaveSpace(spaceId);
      } else {
        await joinSpace(spaceId);
      }
      refetchSpace();
    } catch {
      // Silently handle
    } finally {
      setIsTogglingMembership(false);
    }
  };

  const handleLoadMore = () => {
    setPage((p) => p + 1);
  };

  if (isLoadingSpace) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--color-primary)]" />
      </div>
    );
  }

  if (!space) {
    return (
      <div className="text-center py-16">
        <p className="text-sm text-[var(--color-muted-foreground)]">Space not found.</p>
        <Link
          to="/community"
          className="mt-2 inline-flex items-center gap-1 text-sm text-[var(--color-primary)] hover:underline"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to spaces
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <Link
          to="/community"
          className="inline-flex items-center gap-1 text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-colors mb-3"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          All spaces
        </Link>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">{space.name}</h1>
            {space.description && (
              <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{space.description}</p>
            )}
            <div className="mt-2 flex items-center gap-4 text-xs text-[var(--color-muted-foreground)]">
              <span className="flex items-center gap-1">
                <Users className="h-3.5 w-3.5" />
                {formatNumber(space.memberCount)} members
              </span>
              <span className="flex items-center gap-1">
                <MessageSquare className="h-3.5 w-3.5" />
                {formatNumber(space.postCount)} posts
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleToggleMembership}
            disabled={isTogglingMembership}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              space.isMember
                ? "border border-[var(--color-border)] text-[var(--color-muted-foreground)] hover:border-[var(--color-destructive)]/30 hover:text-[var(--color-destructive)]"
                : "bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary)]/90"
            }`}
          >
            {isTogglingMembership ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : space.isMember ? (
              "Leave Space"
            ) : (
              "Join Space"
            )}
          </button>
        </div>
      </div>

      <AiManagerCard space={space} onUpdated={refetchSpace} />
      <CommunityTemplateCard space={space} onUpdated={refetchSpace} />

      {/* New post form */}
      <NewPostForm spaceId={spaceId!} onCreated={refetchPosts} />

      {/* Post feed */}
      {isLoadingPosts && posts.length === 0 ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-[var(--color-primary)]" />
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-12">
          <MessageSquare className="mx-auto h-8 w-8 text-[var(--color-muted-foreground)]/30" />
          <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
            No posts yet. Be the first to share something.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => (
            <PostCard key={post.id} post={post} onRefresh={refetchPosts} />
          ))}

          {hasMore && (
            <div className="flex justify-center pt-2">
              <button
                type="button"
                onClick={handleLoadMore}
                disabled={isLoadingPosts}
                className="rounded-lg border border-[var(--color-border)] px-6 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-muted)]/30 transition-colors"
              >
                {isLoadingPosts ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Load more"
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
